import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { PaginatedTable } from '@/components/ui/PaginatedTable';
import { useOrg } from '@/contexts/OrgContext';
import { apiPost } from '@/lib/api';
import { getFirestoreDb } from '@/lib/firebase';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';
import { canPostDocuments } from '@finvox/shared/org';

type PayRun = {
  id: string;
  payDate: string;
  netPay: string;
  status: string;
};

export function PayrollPage() {
  const { t } = useTranslation();
  const { orgId, org, currentPeriodId } = useOrg();
  const online = useOnlineStatus();
  const toast = useToast();
  const [importJson, setImportJson] = useState('');

  const canPost = org ? canPostDocuments(org.role) : false;

  const mapDoc = useCallback(
    (id: string, data: Record<string, unknown>) => ({
      id,
      payDate: String(data.payDate ?? ''),
      netPay: String(data.netPay ?? '0.00'),
      status: String(data.status ?? 'draft')
    }),
    []
  );

  const {
    rows: runs,
    loading,
    pageIndex,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    pageSize,
    setPageSize,
    refresh
  } = useFirestorePageQuery<PayRun>({
    orgId,
    collectionPath: 'payRuns',
    orderByField: 'payDate',
    orderDirection: 'desc',
    mapDoc
  });

  async function createFromImport() {
    if (!orgId || !currentPeriodId || !online) return;
    let rows: Record<string, string>[];
    try {
      rows = JSON.parse(importJson) as Record<string, string>[];
      if (!Array.isArray(rows)) throw new Error('Expected array');
    } catch {
      toast.error(t('payroll.invalidJson'), 'payroll');
      return;
    }
    const dry = await apiPost<{
      ok: boolean;
      lines: { label: string; earnings: string; deductions: string }[];
      netPay: string;
      errors: string[];
    }>('/v1/payroll/dry-run', { rows });
    if (!dry.ok) {
      toast.error(dry.errors.join('; ') || t('payroll.invalidJson'), 'payroll');
      return;
    }
    const ref = doc(collection(getFirestoreDb(), 'organizations', orgId, 'payRuns'));
    await setDoc(ref, {
      periodId: currentPeriodId,
      payDate: new Date().toISOString().slice(0, 10),
      lines: dry.lines,
      netPay: dry.netPay,
      status: 'draft',
      createdAt: serverTimestamp()
    });
    setImportJson('');
    toast.success(t('payroll.draftCreated'), 'payroll');
    refresh();
  }

  async function postRun(runId: string) {
    if (!orgId || !online) return;
    try {
      await apiPost('/v1/payroll/post', { orgId, runId });
      toast.success(t('payroll.posted'), 'payroll');
      refresh();
    } catch (e) {
      toastError(toast, e, 'payroll');
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('nav.payroll')}</h1>
      <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('payroll.subtitle')}</p>

      {canPost && (
        <div className="mt-6 space-y-2 rounded-lg border p-4">
          <p className="text-sm font-medium">{t('payroll.importJson')}</p>
          <textarea
            className="h-28 w-full rounded border px-3 py-2 font-mono text-xs"
            value={importJson}
            onChange={(e) => setImportJson(e.target.value)}
            placeholder={'[{"label":"Staff","earnings":"5000.00","deductions":"200.00"}]'}
          />
          <Button onClick={() => void createFromImport()} disabled={!online}>
            {t('payroll.createDraft')}
          </Button>
        </div>
      )}

      <PaginatedTable
        className="mt-6"
        loading={loading}
        pageIndex={pageIndex}
        hasNext={hasNext}
        hasPrev={hasPrev}
        onNext={nextPage}
        onPrev={prevPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-start">{t('journals.date')}</th>
              <th className="p-2 text-end">{t('payroll.netPay')}</th>
              <th className="p-2">{t('journals.status')}</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-b border-[var(--fv-border)]">
                <td className="p-2">{r.payDate}</td>
                <td className="p-2 text-end"><MoneyDisplay value={r.netPay} /></td>
                <td className="p-2">{r.status}</td>
                <td className="p-2">
                  {canPost && r.status === 'draft' && (
                    <Button variant="secondary" onClick={() => void postRun(r.id)} disabled={!online}>
                      {t('payroll.post')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </PaginatedTable>
    </div>
  );
}
